import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';

export interface SetupStatus {
  loading: boolean;
  jiraConnected: boolean;
  teamMembersCount: number;
  /** Members whose role looks like QA/tester (heuristic; matches common Forecast / worklog logic). */
  qaMembersCount: number;
  releasesCount: number;
  periodConfigSet: boolean;
  /** True when Jira works, team has members, releases exist, and period mapping is configured */
  allReady: boolean;
  /** True when setup is sufficient for Forecast Distribute + suggest-from-history heuristics */
  forecastReady: boolean;
  refresh: () => void;
}

/** Aligns with server-side role hints: QA / tester / names containing qa or tester. */
export function countLikelyQaMembers(members: unknown): number {
  if (!Array.isArray(members)) return 0;
  let n = 0;
  for (const m of members) {
    const role =
      m && typeof m === 'object' && 'role' in m ? String((m as { role?: string }).role || '') : '';
    const t = role.trim().toLowerCase();
    if (!t) continue;
    if (t === 'qa' || t === 'tester') {
      n++;
      continue;
    }
    if (t.includes('qa') || t.includes('tester')) n++;
  }
  return n;
}

function isPeriodConfigMeaningful(cfg: Record<string, unknown>): boolean {
  const pm = cfg.periodMappings;
  const hasMappings = pm && typeof pm === 'object' && Object.keys(pm as object).length > 0;
  const hasColumns = Boolean(
    (cfg.devStartColumnId && cfg.devEndColumnId) || (cfg.qaStartColumnId && cfg.qaEndColumnId)
  );
  return Boolean(hasMappings || hasColumns);
}

/**
 * Readiness checks for Release / Sprint plan (capacity views).
 */
export function useSetupStatus(): SetupStatus {
  const [loading, setLoading] = useState(true);
  const [jiraConnected, setJiraConnected] = useState(false);
  const [teamMembersCount, setTeamMembersCount] = useState(0);
  const [qaMembersCount, setQaMembersCount] = useState(0);
  const [releasesCount, setReleasesCount] = useState(0);
  const [periodConfigSet, setPeriodConfigSet] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [boardsRes, teamRes, releasesRes, periodRes] = await Promise.all([
          apiFetch('/api/jira/boards'),
          apiFetch('/api/team-members'),
          apiFetch('/api/releases/list'),
          apiFetch('/api/release-period-config'),
        ]);

        if (cancelled) return;

        setJiraConnected(boardsRes.ok);
        if (teamRes.ok) {
          const t = await teamRes.json().catch(() => ({}));
          const members = (t as { members?: unknown[] }).members;
          const list = Array.isArray(members) ? members : [];
          setTeamMembersCount(list.length);
          setQaMembersCount(countLikelyQaMembers(list));
        } else {
          setTeamMembersCount(0);
          setQaMembersCount(0);
        }

        if (releasesRes.ok) {
          const r = await releasesRes.json().catch(() => ({}));
          const rel = (r as { releases?: unknown[] }).releases;
          setReleasesCount(Array.isArray(rel) ? rel.length : 0);
        } else {
          setReleasesCount(0);
        }

        if (periodRes.ok) {
          const cfg = await periodRes.json().catch(() => ({}));
          setPeriodConfigSet(isPeriodConfigMeaningful(cfg as Record<string, unknown>));
        } else {
          setPeriodConfigSet(false);
        }
      } catch {
        if (!cancelled) {
          setJiraConnected(false);
          setTeamMembersCount(0);
          setQaMembersCount(0);
          setReleasesCount(0);
          setPeriodConfigSet(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const allReady = jiraConnected && teamMembersCount > 0 && releasesCount > 0 && periodConfigSet;
  const forecastReady =
    jiraConnected &&
    teamMembersCount > 0 &&
    qaMembersCount > 0 &&
    releasesCount > 0 &&
    periodConfigSet;

  return {
    loading,
    jiraConnected,
    teamMembersCount,
    qaMembersCount,
    releasesCount,
    periodConfigSet,
    allReady,
    forecastReady,
    refresh,
  };
}
